import { FormEvent, useEffect } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ProfileSubNav } from "./ProfileSubNav"

import type { PageProps } from "@/types/inertia"

interface DetailsProps extends PageProps {
  confirmation?: { token: string; old_email: string; new_email: string }
}

export default function ProfileDetails() {
  const { props } = usePage<DetailsProps>()
  const user = props.current_user
  const errors = props.errors ?? {}
  const confirmation = props.confirmation

  const emailForm = useForm({ email: user?.email ?? "" })
  const confirmForm = useForm({ token: confirmation?.token ?? "" })
  const changed = emailForm.data.email.trim().toLowerCase() !== user?.email

  useEffect(() => { emailForm.setData("email", user?.email ?? "") }, [user?.email])
  useEffect(() => { confirmForm.setData("token", confirmation?.token ?? "") }, [confirmation?.token])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    emailForm.patch("/profile/email", { preserveScroll: true })
  }

  return (
    <>
      <Head title="My details">
        <meta name="description" content="Manage the email address you use to sign in to Flywrite. Confirm changes from your current inbox while keeping your licenses and preferences." />
        <meta property="og:title" content="My details" />
        <meta property="og:description" content="Manage the email address you use to sign in to Flywrite. Confirm changes from your current inbox while keeping your licenses and preferences." />
        <meta name="referrer" content="no-referrer" />
      </Head>
      <AppShell>
        <PageHeader
          title="Profile"
          description="Manage your account."
          tabs={<ProfileSubNav active="details" />}
        />

        {props.flash?.notice && (
          <p role="status" className="mt-6 max-w-md break-words">{props.flash.notice}</p>
        )}
        {props.flash?.alert && <p role="alert" className="mt-6 max-w-md break-words">{props.flash.alert}</p>}

        <section className="mt-10 max-w-md">
          {confirmation ? (
            <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); confirmForm.post("/profile/email/confirm") }}>
              <h2>Confirm email change</h2>
              <p className="break-words">Change your email from <strong>{confirmation.old_email}</strong> to <strong>{confirmation.new_email}</strong>?</p>
              <Button type="submit" disabled={confirmForm.processing}>Confirm email change</Button>
            </form>
          ) : <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email">Email</label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-error" : changed ? "email-help" : undefined}
                value={emailForm.data.email}
                onChange={(e) => emailForm.setData("email", e.target.value)}
              />
              {errors.email && (
                <p id="email-error" role="alert">{errors.email}</p>
              )}
            </div>
            {changed && <p id="email-help">We’ll send a confirmation link to your current email address before making this change.</p>}
            <Button type="submit" disabled={emailForm.processing || !changed}>
              Update email
            </Button>
          </form>}
        </section>
      </AppShell>
    </>
  )
}
